"use client";

import { useCallback } from "react";
import type { MutableRefObject, SyntheticEvent, KeyboardEvent, ClipboardEvent } from "react";
import {
  CellKey,
  CellRegistration,
  EditingCell,
  makeCellKey,
  parseCellKey,
} from "./EditableTableContext";
import { formatValueForCopy, coerceValueForPaste } from "./coerceValueForPaste";

export const ARROW_KEYS = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];

function isInputTarget(e: SyntheticEvent) {
  const target = e.target as HTMLElement | null;
  if (!target) return false;
  return !!target.closest('input, textarea, select, button, [role="button"], [contenteditable]');
}

type KeyboardColumn<DataType> = {
  disableSelection?: boolean;
  onPaste?: (rowId: string, text: string) => void;
  onClear?: (rowId: string) => void;
  copyValue?: (data: DataType) => string;
};

type UseTableKeyboardProps<DataType> = {
  selectedCells: Set<CellKey>;
  anchorCellRef: MutableRefObject<{ rowId: string; colIndex: number } | null>;
  clearSelection: () => void;
  expandSelection: (dir: string) => void;
  advanceCell: (fromRowId: string, fromColIndex: number, reverse?: boolean) => void;
  tabCell: (fromRowId: string, fromColIndex: number, reverse?: boolean) => void;
  editingCell: EditingCell;
  startEdit: (rowId: string, colIndex: number, initialChar?: string) => void;
  cancelEdit: () => void;
  columns: KeyboardColumn<DataType>[];
  data: DataType[];
  getRowId: (row: DataType) => string;
  cellRegistryRef: MutableRefObject<Map<CellKey, CellRegistration>>;
  pinnedTopRow?: { data: DataType };
};

export function useTableKeyboard<DataType>({
  selectedCells,
  anchorCellRef,
  clearSelection,
  expandSelection,
  advanceCell,
  tabCell,
  editingCell,
  startEdit,
  cancelEdit,
  columns,
  data,
  getRowId,
  cellRegistryRef,
  pinnedTopRow,
}: UseTableKeyboardProps<DataType>) {
  const getCopyText = useCallback((): string | null => {
    if (selectedCells.size === 0) return null;
    for (const key of selectedCells) {
      const reg = cellRegistryRef.current.get(key);
      if (reg) return formatValueForCopy(reg.value, reg.type, reg.currencySymbol);
      const { rowId, colIndex: colIdx } = parseCellKey(key);
      const col = columns[colIdx];
      if (col?.copyValue) {
        const row =
          data.find(r => getRowId(r) === rowId) ??
          (pinnedTopRow && getRowId(pinnedTopRow.data) === rowId ? pinnedTopRow.data : undefined);
        if (row) return col.copyValue(row);
      }
    }
    return null;
  }, [selectedCells, columns, data, getRowId, pinnedTopRow, cellRegistryRef]);

  const handleCopyEvent = useCallback(
    (e: ClipboardEvent<HTMLDivElement>) => {
      if (editingCell && isInputTarget(e)) return;
      if (selectedCells.size === 0) return;
      const text = getCopyText();
      if (text !== null) {
        e.clipboardData.setData("text/plain", text);
        e.preventDefault();
      }
    },
    [selectedCells, getCopyText, editingCell]
  );

  const handleDelete = useCallback(() => {
    for (const key of selectedCells) {
      const { rowId, colIndex: colIdx } = parseCellKey(key);
      if (columns[colIdx]?.disableSelection) continue;
      const reg = cellRegistryRef.current.get(key);
      if (reg) {
        reg.onCommit("");
      } else {
        columns[colIdx]?.onClear?.(rowId);
      }
    }
  }, [selectedCells, columns, cellRegistryRef]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        clearSelection();
        cancelEdit();
        return;
      }

      if (isInputTarget(e)) return;

      if (e.key === "Tab") {
        e.preventDefault();
        const anchor = anchorCellRef.current;
        if (anchor) tabCell(anchor.rowId, anchor.colIndex, e.shiftKey);
        return;
      }

      if (ARROW_KEYS.includes(e.key)) {
        if (editingCell) return;
        e.preventDefault();
        if (e.shiftKey) {
          expandSelection(e.key);
        } else {
          const anchor = anchorCellRef.current;
          if (anchor) {
            if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
              tabCell(anchor.rowId, anchor.colIndex, e.key === "ArrowLeft");
            } else {
              advanceCell(anchor.rowId, anchor.colIndex, e.key === "ArrowUp");
            }
          }
        }
        return;
      }

      if (e.key === " " && !editingCell) {
        const anchor = anchorCellRef.current;
        if (anchor) {
          const reg = cellRegistryRef.current.get(makeCellKey(anchor.rowId, anchor.colIndex));
          if (reg?.type === "boolean") {
            e.preventDefault();
            const boolValue = reg.value === true || reg.value === "true";
            reg.onCommit(!boolValue);
            return;
          }
        }
      }

      if (e.key === "Enter" && !editingCell) {
        const anchor = anchorCellRef.current;
        if (anchor) {
          const reg = cellRegistryRef.current.get(makeCellKey(anchor.rowId, anchor.colIndex));
          if (
            reg &&
            (reg.type === "text" ||
              reg.type === "longtext" ||
              reg.type === "number" ||
              reg.type === "percent" ||
              reg.type === "currency" ||
              reg.type === "date" ||
              reg.type === "datetime-local")
          ) {
            e.preventDefault();
            startEdit(anchor.rowId, anchor.colIndex);
            return;
          }
          // Cell has no editable state (e.g. asset/image columns, select columns) — advance.
          e.preventDefault();
          advanceCell(anchor.rowId, anchor.colIndex, e.shiftKey);
        }
        return;
      }

      if ((e.key === "Delete" || e.key === "Backspace") && selectedCells.size > 0) {
        e.preventDefault();
        handleDelete();
        return;
      }

      // Type-to-edit: printable key on a selected text/longtext/currency/number cell enters edit mode
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && !editingCell) {
        const anchor = anchorCellRef.current;
        if (anchor) {
          const reg = cellRegistryRef.current.get(makeCellKey(anchor.rowId, anchor.colIndex));
          if (
            reg &&
            (reg.type === "text" ||
              reg.type === "longtext" ||
              reg.type === "number" ||
              reg.type === "percent" ||
              reg.type === "currency" ||
              reg.type === "date")
          ) {
            e.preventDefault();
            startEdit(anchor.rowId, anchor.colIndex, e.key);
          }
        }
      }
    },
    [
      clearSelection,
      cancelEdit,
      expandSelection,
      handleDelete,
      selectedCells.size,
      editingCell,
      startEdit,
      advanceCell,
      tabCell,
      anchorCellRef,
      cellRegistryRef,
    ]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLDivElement>) => {
      if (editingCell && isInputTarget(e)) return;
      if (selectedCells.size === 0) return;
      e.preventDefault();

      const text = e.clipboardData.getData("text/plain").trim();
      if (!text) return;

      for (const key of selectedCells) {
        const { rowId, colIndex: colIdx } = parseCellKey(key);
        const col = columns[colIdx];
        if (col?.onPaste) {
          col.onPaste(rowId, text);
          continue;
        }
        const reg = cellRegistryRef.current.get(key);
        if (!reg) continue;
        const coerced = coerceValueForPaste(text, reg.type, reg.options, reg.currencySymbol);
        if (coerced !== null) {
          reg.onCommit(coerced);
        }
      }
    },
    [selectedCells, columns, editingCell, cellRegistryRef]
  );

  return { handleKeyDown, handleCopyEvent, handlePaste, handleDelete };
}
